import apiService from './api';

export interface UserSession {
  id: string;
  session_key: string;
  device_name: string;
  device_type: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  browser: string;
  os: string;
  ip_address: string;
  location: string;
  is_active: boolean;
  created_at: string;
  last_activity: string;
  expires_at: string;
  device_display_name: string;
  time_since_activity: string;
  is_current_session: boolean;
  is_expired: boolean;
}

export interface DeviceInfo {
  device_type: string;
  device_name: string;
  browser: string;
  os: string;
  ip_address: string;
  user_agent: string;
  location: string;
}

export interface SessionsResponse {
  sessions: UserSession[];
  total: number;
  current_session_key: string;
}

class SessionsService {
  private baseUrl = '/user-sessions';

  /**
   * Récupère toutes les sessions actives de l'utilisateur
   */
  async getActiveSessions(): Promise<SessionsResponse> {
    try {
      const response = await apiService.get(`${this.baseUrl}/sessions/`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des sessions');
    }
  }

  /**
   * Crée ou met à jour une session
   */
  async createOrUpdateSession(deviceInfo: DeviceInfo): Promise<UserSession> {
    try {
      const response = await apiService.post(`${this.baseUrl}/sessions/`, deviceInfo);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la création/mise à jour de la session:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la création de la session');
    }
  }

  /**
   * Termine une session spécifique
   */
  async terminateSession(sessionId: string): Promise<void> {
    try {
      await apiService.delete(`${this.baseUrl}/sessions/${sessionId}/`);
    } catch (error: any) {
      console.error('Erreur lors de la terminaison de la session:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la terminaison de la session');
    }
  }

  /**
   * Termine toutes les autres sessions sauf la session actuelle
   */
  async terminateAllOtherSessions(): Promise<{ message: string; terminated_count: number }> {
    try {
      const response = await apiService.post(`${this.baseUrl}/sessions/terminate_all_others/`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la terminaison des autres sessions:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la terminaison des sessions');
    }
  }

  /**
   * Met à jour l'activité de la session actuelle
   */
  async updateCurrentSessionActivity(): Promise<{ message: string; last_activity: string }> {
    try {
     
      const response = await apiService.post(`${this.baseUrl}/sessions/update_activity/`);
      
      return response.data;
    } catch (error: any) {
      
      throw new Error(error.response?.data?.error || 'Erreur lors de la mise à jour de l\'activité');
    }
  }

  /**
   * Récupère les informations de la session actuelle
   */
  async getCurrentSession(): Promise<UserSession> {
    try {
      const response = await apiService.get(`${this.baseUrl}/sessions/current_session/`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération de la session actuelle:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la récupération de la session actuelle');
    }
  }

  /**
   * Nettoie les sessions expirées
   */
  async cleanupExpiredSessions(): Promise<{ message: string; cleaned_count: number }> {
    try {
      const response = await apiService.post(`${this.baseUrl}/sessions/cleanup_expired/`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors du nettoyage des sessions:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors du nettoyage des sessions');
    }
  }

  /**
   * Détecte les informations de l'appareil côté client
   */
  detectDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    
    // Détection du type d'appareil
    let deviceType = 'unknown';
    if (/Mobile|Android|iPhone|iPad/i.test(userAgent)) {
      if (/iPad|Tablet/i.test(userAgent)) {
        deviceType = 'tablet';
      } else {
        deviceType = 'mobile';
      }
    } else if (/Windows|Mac|Linux/i.test(userAgent)) {
      deviceType = 'desktop';
    }

    // Détection du navigateur
    let browser = 'Unknown';
    if (/Chrome/i.test(userAgent)) {
      browser = 'Chrome';
    } else if (/Firefox/i.test(userAgent)) {
      browser = 'Firefox';
    } else if (/Safari/i.test(userAgent)) {
      browser = 'Safari';
    } else if (/Edge/i.test(userAgent)) {
      browser = 'Edge';
    }

    // Détection de l'OS
    let os = 'Unknown';
    if (/Windows/i.test(userAgent)) {
      os = 'Windows';
    } else if (/Mac OS/i.test(userAgent)) {
      os = 'macOS';
    } else if (/Linux/i.test(userAgent)) {
      os = 'Linux';
    } else if (/Android/i.test(userAgent)) {
      os = 'Android';
    } else if (/iPhone|iPad/i.test(userAgent)) {
      os = 'iOS';
    }

    // Génération du nom d'appareil
    let deviceName = `${os} - ${browser}`;
    if (deviceType === 'mobile') {
      deviceName = `Mobile (${os})`;
    } else if (deviceType === 'tablet') {
      deviceName = `Tablette (${os})`;
    }

    return {
      device_type: deviceType,
      device_name: deviceName,
      browser,
      os,
      ip_address: '127.0.0.1', // IP par défaut pour le développement local
      user_agent: userAgent,
      location: 'Abidjan, CI' // Par défaut
    };
  }

  /**
   * Initialise automatiquement la session lors du chargement de l'application
   */
  async initializeSession(): Promise<UserSession | null> {
    try {
      const deviceInfo = this.detectDeviceInfo();
      const session = await this.createOrUpdateSession(deviceInfo);
      
      // Programmer la mise à jour périodique de l'activité
      this.scheduleActivityUpdates();
      
      return session;
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de la session:', error);
      return null;
    }
  }

  /**
   * Programme les mises à jour périodiques de l'activité
   */
  private scheduleActivityUpdates(): void {
    // Mettre à jour l'activité toutes les 5 minutes
    setInterval(async () => {
      try {
        await this.updateCurrentSessionActivity();
      } catch (error) {
        console.error('Erreur lors de la mise à jour périodique de l\'activité:', error);
      }
    }, 5 * 60 * 1000); // 5 minutes
  }

  /**
   * Met à jour l'activité lors des interactions utilisateur
   */
  async updateActivityOnInteraction(): Promise<void> {
    try {
      await this.updateCurrentSessionActivity();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'activité:', error);
    }
  }
}

const sessionsService = new SessionsService();
export default sessionsService;
